/**
 * Stage S8 — the footnote / endnote engine, plus the S11 hooks that consume it.
 *
 * Ported from `processFootnotesEarly` and the four
 * `processCommands` rules that belong to the same machinery.
 *
 * Spec: docs/spec/00-MASTER-SPEC.md §3.8 (R-126…R-141) and §3.9 rows R-267…R-271.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * PIPELINE POSITION — load-bearing (spec §1)
 *
 *   S7  expandMacros            ⟶ note bodies are ALREADY macro-expanded when S8 sees them
 *   S8  processFootnotesEarly   ⟵ this module: scans \footnote / \endnote, freezes bodies
 *   S9  processDisplayMath      ⟶ note bodies never reach it (they are held out of `text`)
 *   S10 processInlineMath       ⟶ likewise
 *   S11 processCommands         ⟵ processMarginpar (rule 126), removeReverseMarginpar (127),
 *                                 injectNotes (rules 128-130) run inside this stage
 *
 * The consequence, verified against the oracle: a note body gets ONLY the nine reduced
 * rules at  (R-138). No math, no dashes, no guillemets, no escapes — a note
 * containing `$x^2$ --- \og a \fg` publishes all of that literally (R-139).
 *
 * Everything the notes block picks up AFTER injection (it is spliced into `html` partway
 * through processCommands, at ) is an emergent property of composition, not of this
 * module: `\ref{x}` inside a note body still becomes `<span class="ref">[x]</span>` because
 *  runs after.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * DEFECTS REPRODUCED DELIBERATELY (CLAUDE.md §3 — do not "fix" any of these)
 *
 *   G-2   Mixed footnotes and endnotes produce DUPLICATE VISIBLE NUMBERS — two independent
 *         counters, one shared anchor sequence (R-130).
 *   G-3   Footnotes and endnotes take DIFFERENT DOM SHAPES with the real preamble
 *         (R-133 vs R-134), because only `\makeenmark` is defined there.
 *   G-20  The dead `\endnote` fallback (R-271). Reachable in one real case — see the note
 *         on `replaceEndnoteFallback`.
 *   C-1   `\marginpar` keeps a size class for `\tiny`/`\footnotesize` and loses it for
 *         `\scriptsize`/`\small`, whose tokens were already deleted at.
 *   NEW   `substring`-argument swapping duplicates text around a nested note, and
 *         `$&`/`` $` ``/`$'`/`$$` in a note body are interpreted as JS replacement patterns
 *         at injection time. Both documented at their call sites below.
 */

import { extractBracedContent } from './braces'
import type { DocumentSettings } from './types'

/** One collected note, in document order. Mirrors the anonymous type at . */
export interface CollectedNote {
  type: 'footnote' | 'endnote'
  marker: string
  content: string
}

/** Return shape of {@link processFootnotesEarly}, verbatim from . */
export interface FootnotePassResult {
  /** The document text with every note replaced by its anchor. */
  text: string
  /**
   * The assembled notes block, or `''` when the document has no notes.
   * The source stores this in the module global `globalFootnotesHtml`; the port
   * returns it so the coordinator can put it in the ConversionContext (G-29).
   */
  footnotesHtml: string
}

// ─── Marker generation (R-129, ) ─────────────────────────────────────────────

/**
 * Roman numerals. Ported from.
 *
 * The source iterates a plain object with `for…in`, which walks non-numeric string keys in
 * insertion order. An array of pairs is used here purely to satisfy `noUncheckedIndexedAccess`
 * — the order, and therefore the output, is identical.
 */
function toRoman(num: number, upper: boolean): string {
  const lookup: ReadonlyArray<readonly [string, number]> = [
    ['M', 1000],
    ['CM', 900],
    ['D', 500],
    ['CD', 400],
    ['C', 100],
    ['XC', 90],
    ['L', 50],
    ['XL', 40],
    ['X', 10],
    ['IX', 9],
    ['V', 5],
    ['IV', 4],
    ['I', 1],
  ]
  let roman = ''
  let i = num
  for (const [key, value] of lookup) {
    while (i >= value) {
      roman += key
      i -= value
    }
  }
  return upper ? roman : roman.toLowerCase()
}

/**
 * Alphabetic markers. Ported from.
 *
 * FIDELITY: `num` is the **0-based** index, not the note number — `\alph` note 1 is `a`.
 * It wraps modulo 26 with no carry, so note 27 is `a` again (verified against the oracle).
 */
function toAlpha(num: number, upper: boolean): string {
  const code = (upper ? 65 : 97) + (num % 26)
  return String.fromCharCode(code)
}

/**
 * R-129 — turn a 0-based note index into its visible marker.
 *
 * `style` comes from `settings.footnoteStyle` / `settings.endnoteStyle`, which the settings
 * stage derives from `\renewcommand{\thefootnote}{…}`. Anything the settings
 * stage did not recognise arrives here as a literal string and is returned as-is after
 * stripping leading `\`/`{` and trailing `\`/`}` — so `\renewcommand{\theendnote}{{*}}`
 * marks every note `*`.
 */
export function getNoteMarker(index: number, style: string): string {
  if (style === 'arabic') return (index + 1).toString()
  if (style === 'roman') return toRoman(index + 1, false)
  if (style === 'Roman') return toRoman(index + 1, true)
  if (style === 'alph') return toAlpha(index, false)
  if (style === 'Alph') return toAlpha(index, true)
  if (style === 'symbol') {
    // cycles every 9 notes: note 10 is `*` again.
    const symbols = ['*', '†', '‡', '§', '¶', '||', '**', '††', '‡‡']
    // `index` is never negative, so the `?? ''` arm is unreachable; it exists only to
    // satisfy noUncheckedIndexedAccess without a non-null assertion.
    return symbols[index % symbols.length] ?? ''
  }
  // a literal style string such as `*`, cleaned of wrapping braces/backslashes.
  return style.replace(/^[\\{]+|[\\}]+$/g, '')
}

// ─── S8 — the early note pass (R-126…R-141, ) ────────────────────────────────

/**
 * Stage S8. Replace every `\footnote{…}` / `\endnote{…}` with its anchor and build the
 * notes block. Pure: the source's `globalFootnotesHtml` write becomes the
 * `footnotesHtml` field of the result.
 *
 * Called at  with the extracted document settings, AFTER macro expansion — which is
 * why it sees the `\footnote` / `\endnote` that `\note` expanded into (R-140).
 */
export function processFootnotesEarly(
  text: string,
  settings?: DocumentSettings,
): FootnotePassResult {
  // R-127 / R-128 — what to do when the preamble says NOTHING about numbering.
  //
  // LATEX DEFAULT, honoured deliberately. See docs/LaTeXDefaults.md.
  //   \thefootnote — LaTeX's own classes (article/report/book) define it as
  //                  \arabic{footnote}, so an unmentioned footnote counter is 1, 2, 3.
  //   \theendnote  — the endnotes package defines it as \arabic{endnote}, likewise.
  // Both fallbacks are therefore 'arabic', matching what XeLaTeX would print for a
  // preamble that never mentions them.
  //
  // /!\ 'symbol' USED TO BE THE FOOTNOTE FALLBACK, inherited from the predecessor app, and it
  // is NOT what LaTeX does. It was invisible for as long as a \renewcommand{\thefootnote} was
  // always in scope. On 2026-08-29 a converter stage began deleting the half of \ifpandoc that
  // held that line; the fallback took over and published `*`, `†`, `‡` — cycling every nine
  // notes, so footnotes 9, 18 and 27 all rendered `‡‡` in L'Appel du Seigneur des armées.
  // Inventing a default LaTeX would not have chosen is what made a lost instruction look like
  // a deliberate style. Never reintroduce it: if a document really wants symbols, its preamble
  // says \renewcommand{\thefootnote}{\fnsymbol{footnote}} and R-043 reads it.
  //
  // PREAMBLE LINK: \renewcommand{\thefootnote}{\arabic{footnote}} and
  // \renewcommand{\theendnote}{\arabic{endnote}} (preambuleV3.tex, "Placement des notes").
  // Both are READ (R-043/R-044) and both are kept side by side there, because the DOCUMENT
  // decides per call site whether it writes \footnote{} or \endnote{} — L'Appel uses both.
  const footnoteStyle = settings?.footnoteStyle || 'arabic'
  const endnoteStyle = settings?.endnoteStyle || 'arabic'

  let footnoteIndex = 0
  let endnoteIndex = 0

  /** Every note, both types, in document order. */
  const allNotes: CollectedNote[] = []

  // R-126 — one scanner for both note types. `\s*` allows `\footnote  {…}`.
  const noteRegex = /\\(footnote|endnote)\s*\{/g
  let processedText = ''
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = noteRegex.exec(text)) !== null) {
    // FIDELITY — `String.prototype.substring` SWAPS its arguments when start > end.
    // After a nested note (`\footnote{a \footnote{b} c}`) the outer body has already been
    // consumed, so `lastIndex` overshoots the inner match and this line re-emits the text
    // between them instead of emitting nothing. The inner note is then collected a SECOND
    // time. Confirmed against the oracle; `slice` would silently repair it, so do not
    // substitute it. Pinned by notes.test.ts › 'nested \\footnote duplicates the inner body'.
    processedText += text.substring(lastIndex, match.index)

    const noteType = match[1] ?? ''
    const braceStart = match.index + match[0].length - 1
    const braced = extractBracedContent(text, braceStart)

    if (braced) {
      let marker: string
      let noteCategory: 'footnote' | 'endnote'

      if (noteType === 'footnote') {
        //. The source computes the same marker twice (once for the push, once
        // for the anchor) around the increment; one computation is equivalent and pure.
        marker = getNoteMarker(footnoteIndex, footnoteStyle)
        allNotes.push({ type: 'footnote', marker, content: braced.content })
        footnoteIndex++
        noteCategory = 'footnote'
      } else {
        // the `else` arm, so anything the scanner matched that is not
        // `footnote` is treated as an endnote.
        marker = getNoteMarker(endnoteIndex, endnoteStyle)
        allNotes.push({ type: 'endnote', marker, content: braced.content })
        endnoteIndex++
        noteCategory = 'endnote'
      }

      // R-130 — DEFECT G-2, PRESERVED. The anchor id counts BOTH note types while
      // the visible marker counts only its own type. A document mixing the two therefore
      // shows `1`, `1`, `2`, `2` against ids note-footnote-1, note-endnote-2, note-footnote-3,
      // note-endnote-4. The ids stay unique, so the links work; only the reader is misled.
      const noteIndex = allNotes.length

      let markerHtml = ''
      if (noteCategory === 'footnote') {
        // R-131. `\makefnmark` is NOT defined in the reference preamble, so the
        // default template is used and the fallback branch below fires (G-3).
        const template = settings?.makefnmark || '\\textsuperscript{\\thefootnote}'
        markerHtml = template.replace(/\\thefootnote/g, marker)
      } else {
        // R-132. `\makeenmark` IS defined (pre:213), as
        // `\hspace{0.1em}\textsuperscript{\theenmark}` — which matches the optimised shape.
        const template = settings?.makeenmark || '\\textsuperscript{\\theenmark}'
        markerHtml = template.replace(/\\theenmark/g, marker)
      }

      // R-133 — the "optimised" branch. The `\hspace` is dropped because the
      // spacing lives in the `.footnote-num` / `.endnote-num` CSS.
      // PREAMBLE LINK: \renewcommand{\makeenmark}{\hspace{0.1em}\textsuperscript{\theenmark}}
      // (preambuleV3.tex:465) — "distancer très légèrement la référence de note du texte annoté".
      // HALF READ: the \hspace{0.1em} really is honoured, but by css.ts (.endnote-num margin-left),
      // not here — this branch recognises the shape and then throws the length away. The
      // \textsuperscript half is NOT read either: the <sup> below is hard-coded, and would be
      // emitted whatever the preamble said. Rewriting \makeenmark to drop the superscript would
      // therefore change nothing.
      const optimizedMatch = markerHtml.match(
        /^\s*\\hspace\{([^}]+)\}\s*\\textsuperscript\{\s*(.+?)\s*\}\s*$/,
      )

      const noteId = `note-${noteCategory}-${noteIndex}`
      const refId = `ref-${noteCategory}-${noteIndex}`

      if (optimizedMatch) {
        // Group 2 is mandatory, so `?? ''` is unreachable (noUncheckedIndexedAccess only).
        const content = optimizedMatch[2] ?? ''
        // a bare <sup>, no wrapper span. This is the ENDNOTE shape in Mode A.
        processedText += `<a href="#${noteId}" id="${refId}" class="note-link"><sup class="${noteCategory}-num">${content}</sup></a>`
      } else {
        // R-134 — DEFECT G-3, PRESERVED. Raw LaTeX is emitted inside a wrapper
        // span and left for processCommands to convert ( turns `\textsuperscript{1}`
        // into `<sup>1</sup>`), giving the FOOTNOTE shape
        // `<span class="footnote-num"><sup>1</sup></span>` — a different DOM, and a
        // different CSS path, for the same visual role.
        processedText += `<a href="#${noteId}" id="${refId}" class="note-link"><span class="${noteCategory}-num">${markerHtml}</span></a>`
      }

      lastIndex = braced.endIndex + 1
    } else {
      // R-126 — unbalanced brace: emit the literal `\footnote{` and carry on.
      // The rest of the body stays in the document as plain text.
      processedText += match[0]
      lastIndex = match.index + match[0].length
    }
  }
  processedText += text.substring(lastIndex)

  // R-136 — build the block. Emitted only when at least one note exists.
  let footnotesHtml = ''
  if (allNotes.length > 0) {
    footnotesHtml = '\n<hr class="footnote-separator">\n<div class="footnotes endnotes">\n'
    for (const note of allNotes) {
      // R-138 — EXACTLY nine rules, in this order, all `[^}]+` (so a nested
      // brace defeats them). This is the whole command vocabulary a note body ever gets.
      let content = note.content
      content = content.replace(/\\ts\{([^}]+)\}/g, '<span class="text-underbar">$1</span>')
      content = content.replace(/\\textit\{([^}]+)\}/g, '<em>$1</em>')
      content = content.replace(/\\emph\{([^}]+)\}/g, '<em>$1</em>')
      content = content.replace(/\\textbf\{([^}]+)\}/g, '<strong>$1</strong>')
      content = content.replace(/\\underline\{([^}]+)\}/g, '<u>$1</u>')
      content = content.replace(/\\textsc\{([^}]+)\}/g, '<span class="small-caps">$1</span>')
      content = content.replace(/\\textsuperscript\{([^}]+)\}/g, '<sup>$1</sup>')
      content = content.replace(/~:/g, '&nbsp;:')
      content = content.replace(/~/g, '&nbsp;')

      // R-137. `indexOf` on the object identity, exactly as the source does —
      // every pushed literal is a distinct reference, so this is the note's ordinal.
      const noteIdx = allNotes.indexOf(note) + 1
      const noteId = `note-${note.type}-${noteIdx}`
      const refId = `ref-${note.type}-${noteIdx}`
      // The two note types use different marker classes here too (G-3): `footnote-marker`
      // against `endnote-num`.
      const numClass = note.type === 'footnote' ? 'footnote-marker' : 'endnote-num'
      // PREAMBLE LINK: the `marker + "." + &ensp;` shape here implements
      // \renewcommand{\enoteformat}{\theenmark.\enspace} from preambuleV3.tex.
      // HARD-CODED — that line is never read. The two agree by coincidence, not because one
      // causes the other, so changing \enoteformat in the preamble does nothing until it is
      // actually parsed. If this format changes, update the CONVERTISSEUR note on \enoteformat
      // in preambuleV3.tex.
      // This block reaches the page. Until 2026-08-29 it did not: it was built, stored, and
      // never inserted, because \afficherEndNotes expanded to '' (DF-1). S1b now resolves
      // \ifpandoc to the \else branch, where \afficherEndNotes has a real body ending in
      // \theendnotes, and R-270 below splices this in there.
      footnotesHtml += `<p id="${noteId}" class="footnote-item ${note.type}"><a href="#${refId}" class="note-backlink"><span class="${numClass}">${note.marker}.</span></a>&ensp;${content}</p>\n`
    }
    footnotesHtml += '</div>\n'
  }

  // R-141 — the source assigns to a module global here. The port returns it.
  return { text: processedText, footnotesHtml }
}

// ─── S11 hooks (R-267…R-271, ) ────────────────────────────────────────────────

/**
 * R-267 — `\marginpar{…}` (processCommands rule 126, ).
 *
 * This is how verse numbers reach the left margin in Mode A: the preamble rewrites
 * `\Nverset{n}` into `…\marginpar{\scriptsize n}` (pre:123) and this rule turns that into
 * `<span class="marginpar"> n</span>`.
 *
 * C-1, REPRODUCED — the four size branches are an `if / else if` chain, first match wins,
 * but only two of them are reachable:
 *
 *   `\tiny`         → ` tiny-text`     REACHABLE   (no stripper exists for `\tiny`)
 *   `\scriptsize`   → ` scriptsize`    UNREACHABLE (R-198,, already deleted the token)
 *   `\footnotesize` → ` footnotesize`  REACHABLE   (no stripper exists)
 *   `\small`        → ` small-text`    UNREACHABLE (R-199, )
 *
 * The two unreachable branches are ported anyway: they are dead only because of an ordering
 * accident in processCommands, and deleting them would hide that.
 *
 * Second-order effect, also reproduced: the strippers at  do not eat trailing
 * whitespace but these branches do (`\s*`). So `\marginpar{\scriptsize 5}` keeps its space —
 * `<span class="marginpar"> 5</span>` — while `\marginpar{\tiny 6}` does not.
 *
 * `[^}]+` means a nested brace truncates the argument: `\marginpar{\tiny {5}}` emits
 * `<span class="marginpar tiny-text">{5</span>}`. `\marginpar{}` matches nothing at all
 * (`+` needs one character) and survives to S14, which strips the `{}` and leaves the bare
 * text `\marginpar`.
 *
 * DUPLICATE — commands-text.ts also exports `processMarginpar` (R-267) and folds R-268 into
 * it. The two are behaviourally identical and that module's own comment says to wire this
 * pair. The coordinator must call exactly one of them, and must never `export *` both
 * modules from index.ts: the name collides.
 *
 * @param html text at processCommands rule 126 — i.e. after `\leavevmode` (R-266) and
 *   after the size strippers at.
 */
export function processMarginpar(html: string): string {
  // PREAMBLE LINK: \Nverset (preambuleV3.tex:264), \Npar (:278) and \Nhidden (:289), all three
  // R-267
  // carries verse numbering in production: the preamble's own definitions expand into \marginpar
  // at S7, and this rule turns that into <span class="marginpar">, which css.ts floats into the
  // left margin at the \marginparwidth from preamble line 126. The domain classes .nverset /
  // .npar / .nhidden never appear on the live path (RK-37).
  // /!\ The sizes do NOT all survive: \scriptsize was already deleted at S11 slot 9, so \Nverset
  // and \Nhidden lose their size class while \Npar keeps `tiny-text` (C-1).
  // R-267
  return html.replace(/\\marginpar\{([^}]+)\}/g, (_match: string, content: string): string => {
    let processedContent = content
    let extraClass = ''

    // if/else if, evaluated in this order.
    if (processedContent.includes('\\tiny')) {
      extraClass = ' tiny-text'
      processedContent = processedContent.replace(/\\tiny\s*/g, '')
    } else if (processedContent.includes('\\scriptsize')) {
      extraClass = ' scriptsize'
      processedContent = processedContent.replace(/\\scriptsize\s*/g, '')
    } else if (processedContent.includes('\\footnotesize')) {
      extraClass = ' footnotesize'
      processedContent = processedContent.replace(/\\footnotesize\s*/g, '')
    } else if (processedContent.includes('\\small')) {
      extraClass = ' small-text'
      processedContent = processedContent.replace(/\\small\s*/g, '')
    }

    return `<span class="marginpar${extraClass}">${processedContent}</span>`
  })
}

/**
 * R-268 — `\reversemarginpar` (processCommands rule 127, ). Deleted outright.
 *
 * Note it is never parsed as a *setting* either, so the port models left-margin placement
 * nowhere at all; the margin column is left-hand only because `.marginpar` hard-codes a
 * negative `margin-left`.
 */
export function removeReverseMarginpar(html: string): string {
  // PREAMBLE LINK: \reversemarginpar (preambuleV3.tex:176) — "en bord gauche". NOT READ. This rule
  // R-268
  // anywhere. Left placement is produced instead by the hard-coded `float: left` and negative
  // margin on .marginpar in css.ts. Do not "implement" this command — the placement it asks for is
  // an editorial requirement that is already satisfied, and wiring it up would risk breaking it.
  // R-268
  return html.replace(/\\reversemarginpar/g, '')
}

/**
 * R-269, R-270, R-271 — the notes-block injection points (processCommands rules 128-130,
 * ). Apply as one unit, in this order, at that position.
 *
 * DF-1 — FIXED 2026-08-29. In Mode A `\afficherEndNotes` no longer expands to the empty
 * string: S1b (`macros.ts` `resolveIfPandoc`) resolves the preamble's `\ifpandoc` to the
 * `\else` branch, where `\afficherEndNotes` is
 * `\newpage\setlength{\parindent}{0pt}\theendnotes`. So R-269 below still finds nothing to
 * replace in Mode A — the token was expanded away, as it always was — but what it expanded
 * INTO now ends in `\theendnotes`, which R-270 matches. That is the injection point that
 * fires, and note anchors now resolve to ids that exist. Demonstrated end to end in
 * `ifpandoc.test.ts`.
 *
 * DEFECT, NEW — NOT PREVIOUSLY RECORDED. Both replacements pass `endnotesHtml` as a
 * **string** replacement, so JS replacement patterns inside it are interpreted rather than
 * inserted. A note body containing `$&`, `` $` ``, `$'` or `$$` corrupts the block at
 * injection time — and note bodies are frozen before the math stages, so a `$` in a note
 * survives to here. Verified against the oracle:
 *
 *   `\footnote{co$$t}\theendnotes`  → note reads `co$t`
 *   `\footnote{a$&b}\theendnotes`   → note reads `a\theendnotesb`   ($& = the matched token)
 *   `` \footnote{a$`b}\theendnotes `` → the entire document prefix is spliced into the note
 *   `\footnote{a$'b}\theendnotes`   → the entire document suffix is spliced into the note
 *
 * This is the same class as DF-8 at a different call site. Passing the string
 * straight to `.replace()` reproduces it exactly; a replacer function would silently fix it.
 *
 * @param endnotesHtml `footnotesHtml` from {@link processFootnotesEarly} (`''` if no notes).
 */
export function injectNotes(html: string, endnotesHtml: string): string {
  let out = replaceAfficherEndNotes(html, endnotesHtml) // R-269
  out = replaceTheendnotes(out, endnotesHtml) // R-270
  out = replaceEndnoteFallback(out) // R-271
  return out
}

/**
 * R-269 alone. Dead in Mode A — see {@link injectNotes}.
 *
 * Named `replaceAfficherEndNotes`, deliberately distinct from `applyAfficherEndNotes` in
 * commands-domain.ts, which is a second implementation of the same rule. The coordinator
 * must wire exactly ONE of them; wiring both would splice the block in twice.
 *
 * `|| ''` reproduces `globalFootnotesHtml || ''`: the token is deleted, never
 * replaced by the string `undefined`.
 */
export function replaceAfficherEndNotes(html: string, endnotesHtml: string): string {
  // PREAMBLE LINK: \afficherEndNotes (preambuleV3.tex:487 and :492) and \usepackage{endnotes}
  // (:40). The preamble defines \afficherEndNotes TWICE, in the two branches of \ifpandoc: empty
  // at :487 for Pandoc, real at :492 for XeLaTeX. S1b (macros.ts resolveIfPandoc) now keeps the
  // XeLaTeX one, which ends in \theendnotes — so in Mode A the token is expanded away at S7 and
  // this rule finds nothing, while R-270 below does the actual insertion. This rule is the
  // injection point for Mode B, where no preamble defines the macro and the token survives to
  // here. If the branch decision ever changes, update the CONVERTISSEUR note on the \ifpandoc
  // block in preambuleV3.tex at the same time.
  // R-269 — string replacement, NOT a replacer function: `$` patterns stay live.
  return html.replace(/\\afficherEndNotes/g, endnotesHtml || '')
}

/**
 * R-270 alone — the injection point that fires in Mode A. Since the DF-1 fix
 * (2026-08-29) the reference preamble reaches it: `\afficherEndNotes` expands to the
 * `\else` branch's body, which ends in `\theendnotes`.
 */
export function replaceTheendnotes(html: string, endnotesHtml: string): string {
  // R-270 — same live-`$`-pattern defect as R-269.
  return html.replace(/\\theendnotes/g, endnotesHtml || '')
}

/**
 * R-271 — the `\endnote{…}` fallback (processCommands rule 130, ).
 *
 * G-20 calls this dead code, on the grounds that S8 consumes every `\endnote` before S11
 * runs. ORACLE CONTRADICTS THE SPEC: it is reachable, through an unbalanced brace. When
 * `extractBracedContent` fails at S8 the literal `\endnote{` is put back into the text
 * (R-126), and `[^}]+` here then matches up to the first `}` regardless of nesting:
 *
 *   `X\endnote{a{b} Y`  →  `X<sup class="endnote">[†]</sup> Y`
 *
 * so the note text is deleted and replaced by a dagger that links to nothing. Exported
 * separately from {@link injectNotes} only so the behaviour can be pinned on its own.
 */
export function replaceEndnoteFallback(html: string): string {
  // R-271
  return html.replace(/\\endnote\{([^}]+)\}/g, '<sup class="endnote">[†]</sup>')
}
